import { createContext, useCallback, useContext } from 'react';
import type { JSX, PropsWithChildren } from 'react';

import type { HtmlParserReplaceFn } from '@change-corgi/core-react/html';

import type { TranslateHtmlOptions } from './html';

export type CreateLinkReplacer = (options: Readonly<{ onLinkClick?: () => void }>) => HtmlParserReplaceFn;

type I18nHtmlDefaults = Readonly<{
	/**
	 * Builds the `replace` fn used for links (and any other element it chooses to handle) in every `translateHtml`-family
	 * call and `TranslateHtml`-family component, run for elements left untouched by JSX replacements and by the call's
	 * own `replace` option
	 *
	 * Receives the call's `onLinkClick` option
	 */
	createLinkReplacer?: CreateLinkReplacer;
}>;

const I18nHtmlContext = createContext<I18nHtmlDefaults>({});

/**
 * App-level defaults for the HTML produced by `useI18n().translateHtml` & co - meant to be set up once,
 * near the root of the app, e.g. to render all translated links with the app's link component
 */
export function I18nHtmlProvider({ createLinkReplacer, children }: PropsWithChildren<I18nHtmlDefaults>): JSX.Element {
	return <I18nHtmlContext.Provider value={{ createLinkReplacer }}>{children}</I18nHtmlContext.Provider>;
}

/**
 * Returns a function applying the `I18nHtmlProvider` defaults to `translateHtml`-family options
 *
 * Only needed when calling the standalone `translateHtml`/`translatePluralHtml` functions (e.g. with a custom
 * `translate`) - `useI18n()`'s functions and the `TranslateHtml`-family components already apply them
 */
export function useI18nHtmlOptions(): (options?: TranslateHtmlOptions) => TranslateHtmlOptions | undefined {
	const { createLinkReplacer } = useContext(I18nHtmlContext);

	return useCallback(
		(options?: TranslateHtmlOptions) => {
			if (!createLinkReplacer) return options;
			const defaultReplace = createLinkReplacer({ onLinkClick: options?.onLinkClick });
			const replace = options?.replace;
			return {
				...options,
				/** the call's own `replace` runs first, falling back to the default for elements it leaves untouched */
				replace: replace
					? (element, context) => {
							const replaced = replace(element, context);
							return replaced === undefined ? defaultReplace(element, context) : replaced;
						}
					: defaultReplace,
			};
		},
		[createLinkReplacer],
	);
}
